/**
 * Sales mix cards for the overview. Both read the page scope and apply page filters on click.
 *
 *   <PlatformMix data={platformsResponse} error={err} onRetry={refresh} loading active={scope.platform}
 *                onPick={code => setScope({ platform: code === scope.platform ? '' : code })} />
 *       /api/sales-hub/platforms: share of revenue per platform (human names, zero-revenue hidden),
 *       ₹ change badge, ASP and return rate as a sub-line.
 *   <BrandCategoryMix data={leaderboardResponse} error onRetry loading activeBrand activeCategory
 *                     onBrand={brand => ...} onCategory={(brand, category) => ...} />
 *       /api/sales-hub/leaderboard: brands with share; each brand expands to its categories
 *       (share of brand). Brands/categories with no sales this period are hidden and counted in a note.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Skeleton } from '../../ui/skeleton';
import { LoadError } from '../../LoadError';
import { platformColor, platformLabel } from '../../../config/platforms';
import { DeltaBadge } from '../DeltaBadge';
import { ShareBar } from '../ShareBar';
import { displayName, shortMoney, wholeMoney } from '../format';

const BRAND_COLORS = ['#2563EB', '#059669', '#7C3AED', '#C2410C', '#0891B2', '#DB2777', '#65A30D', '#475569'];
const pctText = value => (value == null ? null : `${Number(value).toFixed(1)}% returned`);

const Card = ({ title, subtitle, testId, loading, children }) => (
  <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid={testId} aria-busy={loading}>
    <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
    </div>
    {children}
  </section>
);

const ListSkeleton = () => <div className="space-y-3 py-1">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-9 w-full" />)}</div>;

export const PlatformMix = ({ data, error, onRetry, loading = false, active = 'ALL', onPick }) => {
  const list = (data?.platforms || []).filter(item => item.revenue > 0);
  const total = data?.totals?.revenue || list.reduce((sum, item) => sum + item.revenue, 0);
  const filtered = active && active !== 'ALL';
  return (
    <Card title="Platform share" subtitle={filtered ? 'Click again to show all platforms' : 'Click a platform to filter the page'} testId="sales-platform-mix" loading={loading}>
      {error && !data ? <LoadError what="platform sales" error={error} onRetry={onRetry} /> : loading && !data ? <ListSkeleton /> : !list.length ? (
        <p className="py-4 text-sm text-gray-500" data-testid="sales-platform-empty">No platform sales in this period. Pick a longer period or clear filters.</p>
      ) : (
        <div className={`space-y-1 ${loading ? 'opacity-60' : ''}`}>
          {list.map(item => {
            const label = item.label || platformLabel(item.platform);
            const sub = [item.avg_selling_price != null ? `Avg ${wholeMoney(item.avg_selling_price)}` : null, pctText(item.return_rate)].filter(Boolean).join(' · ');
            return (
              <ShareBar key={item.platform} label={label} value={shortMoney(item.revenue)} share={total ? (item.revenue / total) * 100 : 0}
                color={platformColor(item.platform)} sublabel={sub || undefined}
                extra={<DeltaBadge current={item.revenue} previous={item.previous_revenue} size="xs" />}
                onClick={() => onPick?.(item.platform)} active={active === item.platform}
                ariaLabel={`${active === item.platform ? 'Remove' : 'Filter by'} ${label}: ${shortMoney(item.revenue)}`}
                testId={`sales-platform-${item.platform}`} />
            );
          })}
        </div>
      )}
    </Card>
  );
};

export const BrandCategoryMix = ({ data, error, onRetry, loading = false, activeBrand = 'ALL', activeCategory = 'ALL', onBrand, onCategory }) => {
  const [open, setOpen] = useState({});
  const brands = (data?.brands || []).filter(item => item.revenue > 0);
  const lost = (data?.brands || []).filter(item => !(item.revenue > 0) && item.previous_revenue > 0);
  const total = data?.totals?.revenue || brands.reduce((sum, item) => sum + item.revenue, 0);
  return (
    <Card title="Brands and categories" subtitle="Click to filter · expand for categories" testId="sales-brand-mix" loading={loading}>
      {error && !data ? <LoadError what="brand sales" error={error} onRetry={onRetry} /> : loading && !data ? <ListSkeleton /> : !brands.length ? (
        <p className="py-4 text-sm text-gray-500" data-testid="sales-brand-empty">No brand sales in this period. Pick a longer period or clear filters.</p>
      ) : (
        <ul className={`space-y-1 ${loading ? 'opacity-60' : ''}`}>
          {brands.map((brand, index) => {
            const categories = (brand.categories || []).filter(item => item.revenue > 0);
            const brandName = displayName(brand.brand);
            const expanded = Boolean(open[brand.brand]);
            const color = BRAND_COLORS[index % BRAND_COLORS.length];
            return (
              <li key={brand.brand}>
                <div className="flex items-start gap-1">
                  <button type="button" aria-expanded={expanded} aria-label={`${expanded ? 'Hide' : 'Show'} categories for ${brandName}`}
                    onClick={() => setOpen(prev => ({ ...prev, [brand.brand]: !expanded }))} disabled={!categories.length}
                    className="mt-1.5 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-30"
                    data-testid={`sales-brand-toggle-${brand.brand}`}>
                    {expanded ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                  </button>
                  <ShareBar className="flex-1 min-w-0" label={brandName} value={shortMoney(brand.revenue)} share={total ? (brand.revenue / total) * 100 : 0}
                    color={color} extra={<DeltaBadge current={brand.revenue} previous={brand.previous_revenue} size="xs" />}
                    sublabel={`${categories.length} ${categories.length === 1 ? 'category' : 'categories'}${brand.return_rate != null ? ` · ${pctText(brand.return_rate)}` : ''}`}
                    onClick={() => onBrand?.(brand.brand)} active={activeBrand === brand.brand && activeCategory === 'ALL'}
                    ariaLabel={`Filter by brand ${brandName}: ${shortMoney(brand.revenue)}`} testId={`sales-brand-${brand.brand}`} />
                </div>
                {expanded && (
                  <ul className="mb-2 ml-8 space-y-0.5 border-l border-gray-100 pl-2" aria-label={`${brandName} categories`}>
                    {categories.map(category => (
                      <li key={category.category}>
                        <ShareBar label={displayName(category.category)} value={shortMoney(category.revenue)} share={brand.revenue ? (category.revenue / brand.revenue) * 100 : 0}
                          color={color} extra={<DeltaBadge current={category.revenue} previous={category.previous_revenue} size="xs" />}
                          onClick={() => onCategory?.(brand.brand, category.category)}
                          active={activeBrand === brand.brand && activeCategory === category.category}
                          ariaLabel={`Filter by ${brandName} ${displayName(category.category)}: ${shortMoney(category.revenue)}, share of brand`}
                          testId={`sales-category-${brand.brand}-${category.category}`} />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {data && lost.length > 0 && (
        <p className="mt-2 text-[11px] text-gray-500" data-testid="sales-brand-lost">
          No sales this period from {lost.map(item => displayName(item.brand)).join(', ')} ({shortMoney(lost.reduce((sum, item) => sum + item.previous_revenue, 0))} before).
        </p>
      )}
    </Card>
  );
};
